import { useEffect, useMemo, useState } from 'react'
import { apiFailure } from '@/lib/apiFailure'
import { watchApi } from './api'
import { retryDelayMs } from './reconnect'
import { stageFromPayload } from './rules'
import type { PublicProfile, StageFace, StageInfo, StageKind, StageLive } from './types'

/** One stage on the event, as the switcher shows it. */
export interface EventStage {
  id: string
  /** Null when the API doesn't say (one from before stage invites, which has only the owner's stage). */
  kind: StageKind | null
  /** Its host's profile: the API's, or for a stage only the room metadata has named so far, its handle and name. */
  host: PublicProfile
  /**
   * Its page as the API gives it (an older server's may still carry a retired `?watch=1`, which `/e/` drops): the room
   * metadata's, the newest word, else the list's; null when neither has one.
   */
  path: string | null
  /** A person stage's user, whose face leads it. */
  ownerUserId: string | null
  /** Its live faces: the room metadata's once it lists stages, the loaded stage's before. */
  members: StageFace[]
  /** The stage as the API sent it; null for one only the room metadata has named so far. */
  info: StageInfo | null
}

export interface EventStages {
  /** The owner's stage first, then people's stages oldest first, then any the metadata named since: the switcher's. */
  stages: EventStage[]
  /** Stages the room metadata listed and has since dropped (a host removed by the organiser): gone, not listed. */
  removed: ReadonlySet<string>
}

const NONE: ReadonlySet<string> = new Set()

/** A stage only the room metadata knows yet (made since the list loaded): named by its handle and name, no photo. */
function fromMetadata(id: string, live: StageLive): EventStage | null {
  const name = live.host?.name || live.host?.handle
  if (!name) return null
  return {
    id,
    kind: live.kind ?? null,
    host: { kind: live.kind === 'organization' ? 'organization' : 'person', handle: live.host?.handle ?? null, name, avatar_url: null },
    path: live.path ?? null,
    ownerUserId: live.ownerUserId ?? null,
    members: live.members,
    info: null,
  }
}

function fromInfo(info: StageInfo, roomStages: Record<string, StageLive> | null): EventStage {
  const loaded = stageFromPayload(info)
  return {
    id: info.id,
    kind: loaded.kind ?? null,
    host: info.host,
    path: roomStages?.[info.id]?.path ?? info.path ?? null,
    ownerUserId: loaded.ownerUserId ?? null,
    members: roomStages ? (roomStages[info.id]?.members ?? []) : loaded.members,
    info,
  }
}

export interface EventStagesInput {
  eventId: string
  /** The room metadata's stages (`useWatchScreen().roomStages`); null while it carries none. */
  roomStages: Record<string, StageLive> | null
  /** The stage on screen: always listed, even before the list loads (or when the API has no list yet). */
  current: StageInfo
}

/**
 * Every stage on the event, for the stage switcher: `GET /public/events/:id/stages` (owner's first, then people's
 * oldest first), live from the room metadata.
 *
 * - A stage the metadata names that the list doesn't have (a host who just accepted an invitation) shows at once from
 *   the metadata's handle and name, and the list loads again for its photo and page.
 * - A stage the metadata listed and has since dropped (the organiser removed its host) is gone. Only one it listed:
 *   the owner's stage is in the metadata only from its first publish, and a new stage's publish can trail the list.
 *   The owner's stage is never gone.
 * - Without the list (it failed, or the API has none yet), the stage on screen and what the metadata names.
 *
 * A failed load tries again after 2, 5, 15, then every 30 s; a 404 (an API without the list) doesn't. Use inside the
 * room. The same array comes back while nothing in it changes (compared by content: a reloaded copy of the stage on
 * screen, or metadata republished for a seat on another stage, gives back the same list).
 */
export function useEventStages({ eventId, roomStages, current }: EventStagesInput): EventStages {
  const [list, setList] = useState<{ eventId: string; stages: StageInfo[] } | null>(null)
  // Asks for the list again (a stage the metadata named that it lacks); each new value is one more load.
  const [reload, setReload] = useState<{ eventId: string; key: string } | null>(null)
  const reloadKey = reload?.eventId === eventId ? reload.key : ''

  useEffect(() => {
    let cancelled = false
    let failures = 0
    let retry: ReturnType<typeof setTimeout> | null = null
    const load = () => {
      retry = null
      watchApi.stages(eventId).then(
        (res) => {
          if (cancelled) return
          const stages = Array.isArray(res.data?.stages) ? res.data.stages.filter((stage) => stage && typeof stage.id === 'string') : []
          setList({ eventId, stages })
        },
        (error: unknown) => {
          if (cancelled) return
          failures += 1
          if (apiFailure(error).status !== 404) retry = setTimeout(load, retryDelayMs(failures))
        },
      )
    }
    load()
    return () => {
      cancelled = true
      if (retry !== null) clearTimeout(retry)
    }
  }, [eventId, reloadKey])

  const listed = list?.eventId === eventId ? list.stages : null

  // Every stage the metadata has listed since the room joined: dropped from it later, it's gone.
  const [seen, setSeen] = useState<{ eventId: string; ids: ReadonlySet<string> }>({ eventId, ids: NONE })
  const seenIds = seen.eventId === eventId ? seen.ids : NONE
  const metadataIds = roomStages ? Object.keys(roomStages) : []
  if (metadataIds.some((id) => !seenIds.has(id))) setSeen({ eventId, ids: new Set([...seenIds, ...metadataIds]) })

  const ownerId = listed?.find((stage) => stage.kind === 'organization')?.id ?? listed?.[0]?.id ?? null
  const removedKey = roomStages
    ? [...seenIds].filter((id) => !(id in roomStages) && id !== ownerId).sort().join('\n')
    : ''
  const removed = useMemo<ReadonlySet<string>>(() => (removedKey ? new Set(removedKey.split('\n')) : NONE), [removedKey])

  // Named by the metadata, missing from the list: load the list again (once for each new set of them).
  const unlisted = listed
    ? metadataIds.filter((id) => !listed.some((stage) => stage.id === id)).sort().join('\n')
    : ''
  useEffect(() => {
    if (!unlisted || !listed) return
    setReload((last) => (last?.eventId === eventId && last.key === unlisted ? last : { eventId, key: unlisted }))
  }, [unlisted, listed, eventId])

  const built = useMemo(() => {
    const infos = listed ?? []
    const known = new Set(infos.map((info) => info.id))
    const all: EventStage[] = infos.map((info) => fromInfo(info, roomStages))
    if (!known.has(current.id)) {
      all.push(fromInfo(current, roomStages))
      known.add(current.id)
    }
    for (const [id, live] of Object.entries(roomStages ?? {})) {
      if (known.has(id)) continue
      const named = fromMetadata(id, live)
      if (named) all.push(named)
    }
    return all.filter((stage) => !removed.has(stage.id))
  }, [listed, roomStages, current, removed])
  // Kept while its content is: the stage on screen reloads as a new object, and the metadata republishes every stage
  // whenever a seat anywhere on the event changes.
  const stagesKey = JSON.stringify(built)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the content, not the copy
  const stages = useMemo(() => built, [stagesKey])

  return useMemo(() => ({ stages, removed }), [stages, removed])
}
